import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { ArrowLeft, Heart, Copy, Sparkles, User, Flame, HeartHandshake, Clock, AlertTriangle, Home, Wallet, Users, TrendingUp } from 'lucide-react';
import { calculateLifePath, calculateCompatibility } from '@/utils/numerologyCalculator';
import { getCompatibilityDescription, getDetailedCompatibility } from '@/utils/numerologyDescriptions';
import { copyToClipboard } from '@/utils/copyToClipboard';
import { calculatePythagorasSquare, compareFamilyLines, compareStabilityLines } from '@/utils/pythagorasSquare';
import UserMenu from '@/components/UserMenu';

export default function CompatibilityPage() {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [partnerData, setPartnerData] = useState({
    firstName: '',
    lastName: '',
    day: '',
    month: '',
    year: ''
  });
  const [compatibility, setCompatibility] = useState(null);
  const [isFormCollapsed, setIsFormCollapsed] = useState(false);

  useEffect(() => {
    const storedData = localStorage.getItem('numerologyData');
    if (!storedData) {
      toast.error('Duomenų nerasta. Prašome pirmiau užpildyti formą.');
      navigate('/skaiciuoti');
    } else {
      setUserData(JSON.parse(storedData));
    }
    
    // Restore partner data from localStorage if exists
    const storedPartnerData = localStorage.getItem('partnerData');
    if (storedPartnerData) {
      const parsed = JSON.parse(storedPartnerData);
      setPartnerData(parsed);
      
      // If we have complete partner data, recalculate compatibility
      const userDataParsed = JSON.parse(storedData);
      if (parsed.firstName && parsed.day && parsed.month && parsed.year && userDataParsed) {
        const day = parseInt(parsed.day);
        const month = parseInt(parsed.month);
        const year = parseInt(parsed.year);
        
        if (day && month && year) {
          const partnerLifePath = calculateLifePath(day, month, year);
          const compatibilityScore = calculateCompatibility(userDataParsed.lifePath, partnerLifePath);
          
          const userSquare = calculatePythagorasSquare(
            parseInt(userDataParsed.day),
            parseInt(userDataParsed.month),
            parseInt(userDataParsed.year)
          );
          const partnerSquare = calculatePythagorasSquare(day, month, year);
          
          const familyComparison = compareFamilyLines(userSquare.square, partnerSquare.square);
          const stabilityComparison = compareStabilityLines(userSquare.square, partnerSquare.square);
          
          setCompatibility({
            score: compatibilityScore,
            partnerLifePath,
            familyComparison,
            stabilityComparison,
            ...parsed
          });
          
          // Auto-collapse form when loading existing data
          setIsFormCollapsed(true);
        }
      }
    }
  }, [navigate]);

  const handleChange = (e) => {
    setPartnerData({
      ...partnerData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!partnerData.firstName || !partnerData.lastName) {
      toast.error('Prašome įvesti partnerio vardą ir pavardę');
      return;
    }
    
    const day = parseInt(partnerData.day);
    const month = parseInt(partnerData.month);
    const year = parseInt(partnerData.year);
    
    if (!day || !month || !year) {
      toast.error('Prašome įvesti teisingą gimimo datą');
      return;
    }
    
    if (day < 1 || day > 31 || month < 1 || month > 12 || year < 1900 || year > new Date().getFullYear()) {
      toast.error('Neteisingos gimimo datos reikšmės');
      return;
    }
    
    // Save partner data to localStorage
    localStorage.setItem('partnerData', JSON.stringify(partnerData));
    
    const partnerLifePath = calculateLifePath(day, month, year);
    const compatibilityScore = calculateCompatibility(userData.lifePath, partnerLifePath);
    
    // Apskaičiuoti Sielos Matricas abiem partneriams
    const userSquare = calculatePythagorasSquare(
      parseInt(userData.day),
      parseInt(userData.month),
      parseInt(userData.year)
    );
    const partnerSquare = calculatePythagorasSquare(day, month, year);
    
    // Palyginti šeimos ir stabilumo linijas
    const familyComparison = compareFamilyLines(userSquare.square, partnerSquare.square);
    const stabilityComparison = compareStabilityLines(userSquare.square, partnerSquare.square);
    
    setCompatibility({
      score: compatibilityScore,
      partnerLifePath,
      familyComparison,
      stabilityComparison,
      ...partnerData
    });
    
    // Auto-collapse form after successful calculation
    setIsFormCollapsed(true);
    
    toast.success('Suderinamumas apskaičiuotas!');
  };

  const copyCompatibility = () => {
    if (!compatibility) return;
    
    const details = getDetailedCompatibility(userData.lifePath, compatibility.partnerLifePath);
    
    const text = `Suderinamumas: ${compatibility.score}%\n\n` +
      `${userData.firstName} (Gyvenimo kelio skaičius: ${userData.lifePath})\n` +
      `${compatibility.firstName} (Gyvenimo kelio skaičius: ${compatibility.partnerLifePath})\n\n` +
      `${getCompatibilityDescription(compatibility.score)}\n\n` +
      `💕 KAS TRAUKIA:\n${details.attraction}\n\n` +
      `⚠️ KAS GALI ERZINTI:\n${details.irritation}\n\n` +
      `🔥 INTYMUMAS:\n${details.intimacy}\n\n` +
      `🎯 LAISVALAIKIS:\n${details.leisure}\n\n` +
      `👨‍👩‍👧‍👦 ŠEIMOS LINIJA (2-5-8):\n${compatibility.familyComparison?.analysis || ''}\n\n` +
      `📊 STABILUMO LINIJA (3-6-9):\n${compatibility.stabilityComparison?.analysis || ''}`;
    
    copyToClipboard(text, 'Suderinamumas');
  };

  if (!userData) return null;

  return (
    <div className="min-h-screen pb-16">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/rezultatai')}
              className="text-foreground/70 hover:text-primary"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Grįžti į rezultatus
            </Button>
            <UserMenu />
          </div>
          
          <div className="text-center">
            <div className="inline-flex items-center justify-center gap-2 mb-4">
              <Heart className="w-8 h-8 text-primary" />
              <h1 className="font-display text-3xl sm:text-4xl text-primary">Suderinamumo Tikrinimas</h1>
            </div>
            <p className="text-muted-foreground text-base">
              Sužinokite, kaip jūsų energijos harmonizuoja
            </p>
          </div>
        </div>

        {/* User Info */}
        <Card className="card-mystical p-6 mb-6 border-primary/30">
          <div className="flex items-center gap-4">
            <User className="w-8 h-8 text-primary" />
            <div>
              <h3 className="font-semibold text-lg text-foreground">{userData.firstName} {userData.lastName}</h3>
              <p className="text-sm text-muted-foreground">
                Gyvenimo kelio skaičius: <span className="text-primary font-semibold">{userData.lifePath}</span>
              </p>
            </div>
          </div>
        </Card>

        {/* Partner Form - Collapsible */}
        <Card className="card-mystical p-8 shadow-card border-border/50 mb-8">
          <div 
            className="flex items-center justify-between cursor-pointer"
            onClick={() => compatibility && setIsFormCollapsed(!isFormCollapsed)}
          >
            <h2 className="font-semibold text-xl text-foreground flex items-center gap-2">
              <Heart className="w-6 h-6 text-primary" />
              Partnerio duomenys
              {compatibility && (
                <span className="text-sm font-normal text-muted-foreground ml-2">
                  ({compatibility.firstName} {compatibility.lastName})
                </span>
              )}
            </h2>
            {compatibility && (
              <Button variant="ghost" size="sm" className="text-muted-foreground">
                {isFormCollapsed ? 'Keisti duomenis ▼' : 'Sutraukti ▲'}
              </Button>
            )}
          </div>
          
          {!isFormCollapsed && (
            <form onSubmit={handleSubmit} className="space-y-6 mt-6">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="firstName" className="text-foreground font-medium mb-2 block">
                    Vardas *
                  </Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    value={partnerData.firstName}
                    onChange={handleChange}
                    placeholder="Įveskite partnerio vardą"
                    className="bg-input border-border text-foreground placeholder:text-muted-foreground"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="lastName" className="text-foreground font-medium mb-2 block">
                    Pavardė *
                  </Label>
                  <Input
                    id="lastName"
                    name="lastName"
                  value={partnerData.lastName}
                  onChange={handleChange}
                  placeholder="Įveskite partnerio pavardę"
                  className="bg-input border-border text-foreground placeholder:text-muted-foreground"
                  required
                />
              </div>
            </div>

            <div>
              <Label className="text-foreground font-medium mb-3 block">
                Gimimo data *
              </Label>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="day" className="text-sm text-muted-foreground mb-2 block">
                    Diena
                  </Label>
                  <Input
                    id="day"
                    name="day"
                    type="number"
                    min="1"
                    max="31"
                    value={partnerData.day}
                    onChange={handleChange}
                    placeholder="DD"
                    className="bg-input border-border text-foreground placeholder:text-muted-foreground text-center"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="month" className="text-sm text-muted-foreground mb-2 block">
                    Mėnuo
                  </Label>
                  <Input
                    id="month"
                    name="month"
                    type="number"
                    min="1"
                    max="12"
                    value={partnerData.month}
                    onChange={handleChange}
                    placeholder="MM"
                    className="bg-input border-border text-foreground placeholder:text-muted-foreground text-center"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="year" className="text-sm text-muted-foreground mb-2 block">
                    Metai
                  </Label>
                  <Input
                    id="year"
                    name="year"
                    type="number"
                    min="1900"
                    max={new Date().getFullYear()}
                    value={partnerData.year}
                    onChange={handleChange}
                    placeholder="YYYY"
                    className="bg-input border-border text-foreground placeholder:text-muted-foreground text-center"
                    required
                  />
                </div>
              </div>
            </div>

            <Button
              type="submit"
              size="lg"
              className="w-full bg-primary hover:bg-primary-glow text-primary-foreground font-semibold py-6 text-lg glow-gold transition-all duration-300"
            >
              <Heart className="w-5 h-5 mr-2" />
              Tikrinti suderinamumą
            </Button>
          </form>
          )}
        </Card>

        {/* Compatibility Results */}
        {compatibility && (
          <Card className="card-mystical p-8 shadow-glow border-primary/30">
            <div className="text-center mb-8">
              <Sparkles className="w-12 h-12 text-primary mx-auto mb-4 animate-pulse-glow" />
              <h2 className="font-display text-2xl sm:text-3xl text-primary mb-2">
                Santykių harmonija ir veidrodžio principas
              </h2>
              <div className="flex items-center justify-center gap-4 mb-4">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-1">{userData.firstName}</p>
                  <p className="text-3xl font-display text-primary">{userData.lifePath}</p>
                </div>
                <Heart className="w-8 h-8 text-primary" />
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-1">{compatibility.firstName}</p>
                  <p className="text-3xl font-display text-primary">{compatibility.partnerLifePath}</p>
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-foreground">Harmonija</span>
                <span className="text-2xl font-display text-primary">{compatibility.score}%</span>
              </div>
              <Progress value={compatibility.score} className="h-3" />
            </div>

            {/* General Description */}
            <div className="bg-secondary/20 border border-primary/20 rounded-lg p-6 mb-6">
              <p className="text-foreground/80 leading-relaxed">
                {getCompatibilityDescription(compatibility.score)}
              </p>
            </div>

            {/* Detailed Compatibility Sections */}
            {(() => {
              const details = getDetailedCompatibility(userData.lifePath, compatibility.partnerLifePath);
              return (
                <div className="space-y-4 mb-6">
                  {/* Mirror Principle - NEW SECTION */}
                  {details.mirror && (
                    <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-lg p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-xl">🪞</span>
                        <h3 className="font-semibold text-foreground">Veidrodžio principas</h3>
                      </div>
                      <div className="text-foreground/80 leading-relaxed text-sm whitespace-pre-line">
                        {details.mirror}
                      </div>
                    </div>
                  )}

                  {/* Attraction */}
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <HeartHandshake className="w-5 h-5 text-green-400" />
                      <h3 className="font-semibold text-foreground">Kas traukia</h3>
                    </div>
                    <p className="text-foreground/80 leading-relaxed text-sm">
                      {details.attraction}
                    </p>
                  </div>

                  {/* Irritation */}
                  <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertTriangle className="w-5 h-5 text-orange-400" />
                      <h3 className="font-semibold text-foreground">Kas gali erzinti</h3>
                    </div>
                    <p className="text-foreground/80 leading-relaxed text-sm">
                      {details.irritation}
                    </p>
                  </div>

                  {/* Intimacy */}
                  <div className="bg-pink-500/10 border border-pink-500/30 rounded-lg p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Flame className="w-5 h-5 text-pink-400" />
                      <h3 className="font-semibold text-foreground">Intymus suderinamumas</h3>
                    </div>
                    <p className="text-foreground/80 leading-relaxed text-sm">
                      {details.intimacy}
                    </p>
                  </div>

                  {/* Leisure */}
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Clock className="w-5 h-5 text-blue-400" />
                      <h3 className="font-semibold text-foreground">Laisvalaikis ir laikas kartu</h3>
                    </div>
                    <p className="text-foreground/80 leading-relaxed text-sm">
                      {details.leisure}
                    </p>
                  </div>

                  {/* Family */}
                  {details.family && (
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <Home className="w-5 h-5 text-purple-400" />
                        <h3 className="font-semibold text-foreground">Šeimos kūrimas</h3>
                      </div>
                      <p className="text-foreground/80 leading-relaxed text-sm">
                        {details.family}
                      </p>
                    </div>
                  )}

                  {/* Finances */}
                  {details.finances && (
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <Wallet className="w-5 h-5 text-yellow-400" />
                        <h3 className="font-semibold text-foreground">Finansinis stabilumas poroje</h3>
                      </div>
                      <p className="text-foreground/80 leading-relaxed text-sm">
                        {details.finances}
                      </p>
                    </div>
                  )}

                  {/* Sielos Matricos linijų palyginimas */}
                  {compatibility.familyComparison && (
                    <div className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border border-indigo-500/30 rounded-lg p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <Users className="w-5 h-5 text-indigo-400" />
                        <h3 className="font-semibold text-foreground">Šeimos linija (2-5-8)</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          compatibility.familyComparison.compatibility === 'puikus' ? 'bg-green-500/20 text-green-400' :
                          compatibility.familyComparison.compatibility === 'labai_geras' ? 'bg-emerald-500/20 text-emerald-400' :
                          compatibility.familyComparison.compatibility === 'geras' ? 'bg-blue-500/20 text-blue-400' :
                          compatibility.familyComparison.compatibility === 'vidutinis' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-orange-500/20 text-orange-400'
                        }`}>
                          {compatibility.familyComparison.compatibility === 'puikus' ? 'Puikus' :
                           compatibility.familyComparison.compatibility === 'labai_geras' ? 'Labai geras' :
                           compatibility.familyComparison.compatibility === 'geras' ? 'Geras' :
                           compatibility.familyComparison.compatibility === 'vidutinis' ? 'Vidutinis' :
                           compatibility.familyComparison.compatibility === 'galimas' ? 'Galimas' : 'Sudėtingas'}
                        </span>
                      </div>
                      <p className="text-foreground/80 leading-relaxed text-sm whitespace-pre-line">
                        {compatibility.familyComparison.analysis}
                      </p>
                    </div>
                  )}

                  {compatibility.stabilityComparison && (
                    <div className="bg-gradient-to-r from-teal-500/10 to-cyan-500/10 border border-teal-500/30 rounded-lg p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <TrendingUp className="w-5 h-5 text-teal-400" />
                        <h3 className="font-semibold text-foreground">Stabilumo linija (3-6-9)</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          compatibility.stabilityComparison.compatibility === 'puikus' ? 'bg-green-500/20 text-green-400' :
                          compatibility.stabilityComparison.compatibility === 'labai_geras' ? 'bg-emerald-500/20 text-emerald-400' :
                          compatibility.stabilityComparison.compatibility === 'geras' ? 'bg-blue-500/20 text-blue-400' :
                          compatibility.stabilityComparison.compatibility === 'vidutinis' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-orange-500/20 text-orange-400'
                        }`}>
                          {compatibility.stabilityComparison.compatibility === 'puikus' ? 'Puikus' :
                           compatibility.stabilityComparison.compatibility === 'labai_geras' ? 'Labai geras' :
                           compatibility.stabilityComparison.compatibility === 'geras' ? 'Geras' :
                           compatibility.stabilityComparison.compatibility === 'vidutinis' ? 'Vidutinis' :
                           compatibility.stabilityComparison.compatibility === 'galimas' ? 'Galimas' : 'Sudėtingas'}
                        </span>
                      </div>
                      <p className="text-foreground/80 leading-relaxed text-sm whitespace-pre-line">
                        {compatibility.stabilityComparison.analysis}
                      </p>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Copy Button */}
            <Button
              onClick={copyCompatibility}
              size="lg"
              variant="outline"
              className="w-full border-primary/30 text-primary hover:bg-primary/10"
            >
              <Copy className="w-5 h-5 mr-2" />
              Kopijuoti rezultatus
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
}
